import React, { useMemo, useState } from 'react';
import {
  Checkmark20Regular,
  LockClosed16Filled,
  Sparkle20Regular,
} from '@fluentui/react-icons';
import { ElicitationField, ElicitationRequest } from '@/types/api';

/* ------------------------------------------------------------------ */
/*  PaymentForm — the card step of complete_checkout. The payment is   */
/*  a demonstration: nothing is debited, the test card is pre-filled   */
/*  and one button fills it in. Declining means "pay later".           */
/* ------------------------------------------------------------------ */

interface PaymentFormProps {
  request: ElicitationRequest;
  disabled?: boolean;
  onSubmit: (content: Record<string, string>) => void;
  onDecline: () => void;
}

type Fields = Record<string, ElicitationField>;

const groupDigits = (value: string) => value.replace(/\D/g, '').slice(0, 19).replace(/(.{4})/g, '$1 ').trim();

const formatExpiry = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 4);
  return digits.length <= 2 ? digits : `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

const eur = (cents: number) => (cents / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });

const PaymentForm: React.FC<PaymentFormProps> = ({ request, disabled, onSubmit, onDecline }) => {
  const fields = (request.requested_schema?.properties ?? {}) as Fields;
  const first = Object.values(fields)[0] ?? {};
  const en = (first['x-lang'] ?? 'fr').startsWith('en');
  const amount = first['x-amount'];
  const demoLabel = first['x-demo-label'] ?? (en ? 'Use the demo card' : 'Utiliser la carte de démonstration');
  const declineLabel = first['x-decline-label'] ?? (en ? 'Pay later' : 'Payer plus tard');
  const required = request.requested_schema?.required ?? [];

  const defaults = useMemo(() => {
    const out: Record<string, string> = {};
    for (const [name, def] of Object.entries(fields)) out[name] = def.default == null ? '' : String(def.default);
    return out;
  }, [request.key]);

  // The demo card is offered, not imposed: the fields start empty.
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(Object.keys(fields).map((name) => [name, ''])),
  );
  const [submitted, setSubmitted] = useState<'paid' | 'later' | null>(null);

  const set = (name: string, value: string) => setValues((s) => ({ ...s, [name]: value }));
  const missing = required.some((name) => !values[name]?.trim());

  const pay = () => {
    if (missing) return;
    setSubmitted('paid');
    onSubmit(values);
  };
  const later = () => {
    setSubmitted('later');
    onDecline();
  };

  const order = Object.entries(fields).sort((a, b) => (a[1]['x-order'] ?? 0) - (b[1]['x-order'] ?? 0));
  const lines = (request.message || '').split('\n').filter(Boolean);

  if (submitted) {
    return (
      <div className="receipt receipt-done">
        <div className="receipt-actions receipt-sent">
          <Checkmark20Regular />
          {submitted === 'paid'
            ? en ? 'Payment sent' : 'Paiement envoyé'
            : en ? 'Paying later' : 'Paiement plus tard'}
        </div>
      </div>
    );
  }

  return (
    <div className="receipt pay-card" role="dialog" aria-label={en ? 'Card payment' : 'Paiement par carte'}>
      <div className="receipt-head">
        <div className="receipt-kicker"><LockClosed16Filled /> {en ? 'Secure payment' : 'Paiement sécurisé'}</div>
        {amount != null && (
          <div className="receipt-total">
            <span className="receipt-amount">{eur(amount)}</span>
            <span className="receipt-note">{lines[1] || (en ? 'Demo: no real charge.' : 'Démonstration : aucun débit réel.')}</span>
          </div>
        )}
      </div>

      <div className="pay-body">
        <button type="button" className="of-btn of-btn-ghost pay-demo" onClick={() => setValues({ ...defaults })} disabled={disabled}>
          <Sparkle20Regular /> {demoLabel}
        </button>

        <div className="pay-grid">
          {order.map(([name, def]) => {
            const isNumber = def['x-widget'] === 'card-number';
            const isExpiry = def['x-widget'] === 'card-expiry';
            const isCvc = def['x-widget'] === 'card-cvc';
            const onChange = (raw: string) =>
              set(name, isNumber ? groupDigits(raw) : isExpiry ? formatExpiry(raw) : isCvc ? raw.replace(/\D/g, '').slice(0, 4) : raw);
            return (
              <div key={name} className={`of-field${def['x-half'] ? '' : ' of-field-wide'}`}>
                <label className="of-label" htmlFor={`pay-${name}`}>
                  {def.title}
                  {required.includes(name) && <span className="of-req">*</span>}
                </label>
                <input
                  id={`pay-${name}`}
                  className="of-input"
                  type="text"
                  inputMode={isNumber || isExpiry || isCvc ? 'numeric' : 'text'}
                  autoComplete={def['x-autocomplete'] ?? (isNumber ? 'cc-number' : isExpiry ? 'cc-exp' : isCvc ? 'cc-csc' : undefined)}
                  placeholder={def['x-placeholder']}
                  value={values[name] ?? ''}
                  onChange={(e) => onChange(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !missing) pay(); }}
                  disabled={disabled}
                />
                {def.description && <span className="of-hint">{def.description}</span>}
              </div>
            );
          })}
        </div>
      </div>

      <div className="receipt-actions receipt-actions-split">
        <button type="button" className="of-link" onClick={later} disabled={disabled}>{declineLabel}</button>
        <button type="button" className="of-btn of-btn-primary" onClick={pay} disabled={disabled || missing}>
          <LockClosed16Filled /> {amount != null ? (en ? `Pay ${eur(amount)}` : `Payer ${eur(amount)}`) : en ? 'Pay' : 'Payer'}
        </button>
      </div>
    </div>
  );
};

export default PaymentForm;
